import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Button, Table, Badge,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Nav, NavItem, NavLink, TabContent, TabPane
} from 'reactstrap';
import { Plus, RefreshCw, Play, Pause, Calendar, History, TestTube, Download, Edit, Trash2 } from 'lucide-react';
import API from '../utils/api';
import SourceForm from '../components/SourceForm';
import SourceImport from '../components/SourceImport';
import SourceSchedule from '../components/SourceSchedule';
import SourceHistory from '../components/SourceHistory';

const Sources = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  const [importModal, setImportModal] = useState(false);
  const [scheduleModal, setScheduleModal] = useState(false);
  const [historyModal, setHistoryModal] = useState(false);
  const [deleteDataModal, setDeleteDataModal] = useState(false);
  const [dataStats, setDataStats] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [deleteAll, setDeleteAll] = useState(false);

  useEffect(() => {
    loadSources();
  }, []);

  const loadSources = async () => {
    try {
      setLoading(true);
      const response = await API.get('/sources');
      setSources(response.data.sources);
    } catch (error) {
      console.error('Failed to load sources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedSource(null);
    setEditMode(false);
    setModal(true);
  };

  const handleEdit = (source) => {
    setSelectedSource(source);
    setEditMode(true);
    setModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this source?')) {
      try {
        await API.delete(`/sources/${id}`);
        loadSources();
      } catch (error) {
        console.error('Failed to delete source:', error);
      }
    }
  };

  const handleTest = async (source) => {
    try {
      const response = await API.post(`/sources/${source.id}/test`);
      alert(response.data.success ?
        'Connection test successful!' :
        `Connection test failed: ${response.data.error}`
      );
    } catch (error) {
      alert('Connection test failed: ' + error.message);
    }
  };

  const handleImport = (source) => {
    setSelectedSource(source);
    setImportModal(true);
  };

  const handleSchedule = (source) => {
    setSelectedSource(source);
    setScheduleModal(true);
  };

  const handleHistory = (source) => {
    setSelectedSource(source);
    setHistoryModal(true);
  };

  const handleSaveSource = async (sourceData) => {
    try {
      if (editMode) {
        await API.put(`/sources/${selectedSource.id}`, sourceData);
      } else {
        await API.post('/sources', sourceData);
      }
      setModal(false);
      loadSources();
    } catch (error) {
      console.error('Failed to save source:', error);
      alert('Failed to save source: ' + error.message);
    }
  };

  const handleDeleteData = async (source) => {
    try {
      setSelectedSource(source);
      setDataStats(null);
      setSelectedDate('');
      setDeleteAll(false);

      // Load data statistics
      const response = await API.get(`/sources/${source.id}/data-stats`);
      setDataStats(response.data);
      setDeleteDataModal(true);
    } catch (error) {
      console.error('Failed to load source data stats:', error);
      alert('Failed to load source data statistics');
    }
  };

  const handleConfirmDeleteData = async () => {
    if (!selectedSource) return;

    const confirmMessage = deleteAll
      ? `Are you sure you want to delete ALL ${dataStats?.total_records || 0} records from "${selectedSource.name}"?`
      : `Are you sure you want to delete ${getRecordCountForDate(selectedDate)} records from "${selectedSource.name}" for date ${selectedDate}?`;

    if (!window.confirm(confirmMessage)) return;

    try {
      const payload = {
        confirm: true,
        delete_all: deleteAll,
        date: deleteAll ? null : selectedDate
      };

      const response = await API.post(`/sources/${selectedSource.id}/delete-data`, payload);

      if (response.data.success) {
        alert(response.data.message);
        setDeleteDataModal(false);
        // Reload data statistics
        const updatedResponse = await API.get(`/sources/${selectedSource.id}/data-stats`);
        setDataStats(updatedResponse.data);
      }
    } catch (error) {
      console.error('Failed to delete source data:', error);
      alert('Failed to delete source data: ' + (error.response?.data?.error || error.message));
    }
  };

  const getRecordCountForDate = (date) => {
    if (!dataStats || !date) return 0;
    const dateEntry = dataStats.date_distribution.find(d => d.date === date);
    return dateEntry ? dateEntry.count : 0;
  };

  const getSourceTypeColor = (type) => {
    const colors = {
      http: 'bg-dark text-primary border border-primary',
      ftp: 'bg-dark text-info border border-info',
      ftps: 'bg-dark text-secondary border border-secondary',
      s3: 'bg-dark text-warning border border-warning',
      api: 'bg-dark text-success border border-success',
      file: 'bg-dark text-light border border-secondary'
    };
    return colors[type] || 'bg-dark text-light border border-secondary';
  };

  const getFormatBadgeClass = (format) => {
    const colors = {
      json: 'bg-dark text-success border border-success',
      csv: 'bg-dark text-info border border-info',
      tsv: 'bg-dark text-info border border-info',
      xml: 'bg-dark text-warning border border-warning'
    };
    return colors[format] || 'bg-dark text-light border border-secondary';
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <h2 className="page-heading">
            <i className="fas fa-database page-heading-icon"></i>
            Domain Sources Management
          </h2>
          <small className="text-muted">Configure and manage domain data sources</small>
        </Col>
        <Col xs="auto">
          <Button color="primary" size="sm" onClick={handleCreate}>
            <Plus size={16} className="me-1" /> Add Source
          </Button>
          <Button color="secondary" size="sm" className="ms-2" onClick={loadSources}>
            <RefreshCw size={16} className="me-1" /> Refresh
          </Button>
        </Col>
      </Row>

      <Card className="bg-dark border-secondary">
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : sources.length === 0 ? (
            <div className="text-center py-5">
              <h5 className="text-muted">No sources configured</h5>
              <p className="text-muted">Click "Add Source" to configure your first data source</p>
            </div>
          ) : (
            <Table dark hover responsive size="sm">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Format</th>
                  <th>EPP Categories</th>
                  <th>Status</th>
                  <th>Schedule</th>
                  <th>Last Import</th>
                  <th>Total Domains</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sources.map(source => (
                  <tr key={source.id}>
                    <td>
                      <strong>{source.name}</strong>
                      {source.description && (
                        <small className="d-block text-muted">{source.description}</small>
                      )}
                    </td>
                    <td>
                      <Badge className={getSourceTypeColor(source.type)}>
                        {source.type?.toUpperCase()}
                      </Badge>
                    </td>
                    <td>
                      <Badge className={getFormatBadgeClass(source.data_format)}>
                        {source.data_format?.toUpperCase()}
                      </Badge>
                      {source.archive_type !== 'none' && (
                        <Badge className="bg-dark text-light border border-secondary ms-1">
                          {source.archive_type?.toUpperCase()}
                        </Badge>
                      )}
                    </td>
                    <td>
                      {source.epp_categories && source.epp_categories.length > 0 ? (
                        <div>
                          {source.epp_categories.slice(0, 2).map(category => (
                            <Badge key={category.id} className="bg-dark text-info border border-info me-1 mb-1" style={{ fontSize: '0.7em' }}>
                              {category.name}
                            </Badge>
                          ))}
                          {source.epp_categories.length > 2 && (
                            <Badge className="bg-dark text-secondary border border-secondary" style={{ fontSize: '0.7em' }}>
                              +{source.epp_categories.length - 2} more
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted" style={{ fontSize: '0.8em' }}>None</span>
                      )}
                    </td>
                    <td>
                      {source.is_active ? (
                        <Badge className="bg-dark text-success border border-success">Active</Badge>
                      ) : (
                        <Badge className="bg-dark text-danger border border-danger">Inactive</Badge>
                      )}
                    </td>
                    <td>
                      {source.schedule_enabled ? (
                        <Badge className="bg-dark text-info border border-info">
                          {source.schedule_cron || `Every ${source.schedule_interval_minutes} min`}
                        </Badge>
                      ) : (
                        <Badge className="bg-dark text-light border border-secondary">Disabled</Badge>
                      )}
                    </td>
                    <td>
                      {source.last_import_at ? (
                        <>
                          {new Date(source.last_import_at).toLocaleDateString()}
                          {source.last_import_success ? (
                            <Badge className="bg-dark text-success border border-success ms-1">✓</Badge>
                          ) : (
                            <Badge className="bg-dark text-danger border border-danger ms-1">✗</Badge>
                          )}
                        </>
                      ) : (
                        <span className="text-muted">Never</span>
                      )}
                    </td>
                    <td>{source.total_domains_imported || 0}</td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        <Button
                          color="outline-info"
                          size="sm"
                          onClick={() => handleTest(source)}
                          title="Test Connection"
                        >
                          <TestTube size={14} />
                        </Button>
                        <Button
                          color="outline-success"
                          size="sm"
                          onClick={() => handleImport(source)}
                          title="Import Data"
                        >
                          <Download size={14} />
                        </Button>
                        <Button
                          color="outline-warning"
                          size="sm"
                          onClick={() => handleSchedule(source)}
                          title="Schedule"
                        >
                          <Calendar size={14} />
                        </Button>
                        <Button
                          color="outline-secondary"
                          size="sm"
                          onClick={() => handleHistory(source)}
                          title="History"
                        >
                          <History size={14} />
                        </Button>
                        <Button
                          color="outline-info"
                          size="sm"
                          onClick={() => handleDeleteData(source)}
                          title="Delete Data"
                        >
                          <i className="fas fa-database" style={{fontSize: '12px'}} />
                        </Button>
                        <Button
                          color="outline-primary"
                          size="sm"
                          onClick={() => handleEdit(source)}
                          title="Edit"
                        >
                          <Edit size={14} />
                        </Button>
                        <Button
                          color="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(source.id)}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      </Card>

      {/* Source Form Modal */}
      <Modal
        isOpen={modal}
        toggle={() => setModal(false)}
        size="lg"
        className="modal-dark"
      >
        <ModalHeader toggle={() => setModal(false)}>
          {editMode ? 'Edit Source' : 'Add New Source'}
        </ModalHeader>
        <ModalBody>
          <SourceForm
            source={selectedSource}
            onSave={handleSaveSource}
            onCancel={() => setModal(false)}
          />
        </ModalBody>
      </Modal>

      {/* Import Modal */}
      {selectedSource && (
        <Modal
          isOpen={importModal}
          toggle={() => setImportModal(false)}
          size="lg"
          className="modal-dark"
        >
          <ModalHeader toggle={() => setImportModal(false)}>
            Import from {selectedSource.name}
          </ModalHeader>
          <ModalBody>
            <SourceImport
              source={selectedSource}
              onClose={() => {
                setImportModal(false);
                loadSources();
              }}
            />
          </ModalBody>
        </Modal>
      )}

      {/* Schedule Modal */}
      {selectedSource && (
        <Modal
          isOpen={scheduleModal}
          toggle={() => setScheduleModal(false)}
          className="modal-dark"
        >
          <ModalHeader toggle={() => setScheduleModal(false)}>
            Schedule {selectedSource.name}
          </ModalHeader>
          <ModalBody>
            <SourceSchedule
              source={selectedSource}
              onSave={() => {
                setScheduleModal(false);
                loadSources();
              }}
              onClose={() => setScheduleModal(false)}
            />
          </ModalBody>
        </Modal>
      )}

      {/* History Modal */}
      {selectedSource && (
        <Modal
          isOpen={historyModal}
          toggle={() => setHistoryModal(false)}
          size="xl"
          className="modal-dark"
        >
          <ModalHeader toggle={() => setHistoryModal(false)}>
            Import History - {selectedSource.name}
          </ModalHeader>
          <ModalBody>
            <SourceHistory
              source={selectedSource}
              onClose={() => setHistoryModal(false)}
            />
          </ModalBody>
        </Modal>
      )}

      {/* Delete Data Modal */}
      {selectedSource && (
        <Modal
          isOpen={deleteDataModal}
          toggle={() => setDeleteDataModal(false)}
          className="modal-dark"
        >
          <ModalHeader toggle={() => setDeleteDataModal(false)}>
            Delete Data - {selectedSource.name}
          </ModalHeader>
          <ModalBody>
            {dataStats ? (
              <div>
                <div className="mb-4">
                  <h6 className="text-white">Data Statistics</h6>
                  <div className="bg-secondary p-3 rounded">
                    <div className="row">
                      <div className="col-md-6">
                        <strong className="text-info">Total Records:</strong>
                        <span className="text-white ms-2">{dataStats.total_records?.toLocaleString() || 0}</span>
                      </div>
                      <div className="col-md-6">
                        <strong className="text-info">Date Range:</strong>
                        <span className="text-white ms-2">
                          {dataStats.date_range?.min_date || 'N/A'} - {dataStats.date_range?.max_date || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <h6 className="text-white">Delete Options</h6>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="deleteType"
                      id="deleteAll"
                      checked={deleteAll}
                      onChange={() => {
                        setDeleteAll(true);
                        setSelectedDate('');
                      }}
                    />
                    <label className="form-check-label text-white" htmlFor="deleteAll">
                      Delete All Records ({dataStats.total_records?.toLocaleString() || 0} records)
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="deleteType"
                      id="deleteSpecific"
                      checked={!deleteAll}
                      onChange={() => setDeleteAll(false)}
                    />
                    <label className="form-check-label text-white" htmlFor="deleteSpecific">
                      Delete Records for Specific Date
                    </label>
                  </div>
                </div>

                {!deleteAll && (
                  <div className="mb-4">
                    <h6 className="text-white">Select Date</h6>
                    <select
                      className="form-select bg-dark text-white"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                    >
                      <option value="">Select a date...</option>
                      {dataStats.date_distribution?.map(dateEntry => (
                        <option key={dateEntry.date} value={dateEntry.date}>
                          {dateEntry.date} ({dateEntry.count.toLocaleString()} records)
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="text-center">
                  <Button
                    color="danger"
                    onClick={handleConfirmDeleteData}
                    disabled={!deleteAll && !selectedDate}
                  >
                    <i className="fas fa-trash me-2"></i>
                    {deleteAll ? 'Delete All Data' : 'Delete Selected Data'}
                  </Button>
                  <Button
                    color="secondary"
                    className="ms-2"
                    onClick={() => setDeleteDataModal(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading statistics...</span>
                </div>
                <p className="text-muted mt-2">Loading data statistics...</p>
              </div>
            )}
          </ModalBody>
        </Modal>
      )}
    </Container>
  );
};

export default Sources;